import handleCallCreated from '@/services/notifications/event-handlers/call-created';
import handleCallEnded from '@/services/notifications/event-handlers/call-ended';
import handleCallRemoved from '@/services/notifications/event-handlers/call-removed';
import handleCallStarted from '@/services/notifications/event-handlers/call-started';
import handleCallbackCreated from '@/services/notifications/event-handlers/callback-created';
import handleClientDuplicatesUpdated from '@/services/notifications/event-handlers/client-duplicates-updated';
import handleDataImportFileStatusUpdated from '@/services/notifications/event-handlers/data-import-file-status-updated';
import handleSystemSettingValueChanged from '@/services/notifications/event-handlers/system-setting-value-changed';
import handleUserArchived from '@/services/notifications/event-handlers/user-archived';
import handleUserAssignedToCall from '@/services/notifications/event-handlers/user-assigned-to-call';
import handleUserAssignedToPartnerPractice from '@/services/notifications/event-handlers/user-assigned-to-partner-practice';
import handleUserEmailChanged from '@/services/notifications/event-handlers/user-email-changed';
import handleUserFirstNameChanged from '@/services/notifications/event-handlers/user-first-name-changed';
import handleUserLastNameChanged from '@/services/notifications/event-handlers/user-last-name-changed';
import handleUserRemovedFromPartnerPractice from '@/services/notifications/event-handlers/user-removed-from-partner-practice';
import handleUserRoleAssigned from '@/services/notifications/event-handlers/user-role-assigned';
import handleUserRoleRemoved from '@/services/notifications/event-handlers/user-role-removed';
import handleUserUnassignedFromCall from '@/services/notifications/event-handlers/user-unassigned-from-call';
import userManager from '@/services/user-manager/user-manager';
import { HubConnection, HubConnectionBuilder, JsonHubProtocol } from '@aspnet/signalr';

class WebsocketManager {
    private socket: HubConnection | null = null;

    public async startAsync(): Promise<void> {
        const baseUrl = process.env.NODE_ENV !== 'production'
            ? `${process.env.VUE_APP_API_URL}`
            : `https://api.${window.location.host}`;

        this.socket = new HubConnectionBuilder()
            .withUrl(`${baseUrl}/v1/notification`,
                {
                    accessTokenFactory: async (): Promise<string> => {
                        const user = await userManager.getUser();

                        if (user == null) {
                            return '';
                        }

                        return user.access_token;
                    },
                })
            .withHubProtocol(new JsonHubProtocol())
            .build();

        await this.setupSocketEvents(this.socket);

        this.socket.onclose(this.handleSocketCloseOrError.bind(this));
        await this.startSocketWithRetryAsync();
    }

    private async startSocketWithRetryAsync(): Promise<void> {
        try {
            const user = await userManager.getUser();
            if (user == null) {
                await new Promise((resolve) => setTimeout(() => {
                    resolve();
                },
                    10000));
                await this.startSocketWithRetryAsync();
            } else {
                await this.socket!.start();
            }
        } catch (e) {
            await this.handleSocketCloseOrError(e);
        }
    }

    private async handleSocketCloseOrError(error: Error | undefined): Promise<void> {
        await new Promise((resolve) => setTimeout(() => {
            resolve();
        },
            10000));
        await this.startAsync();
    }

    private async setupSocketEvents(hub: HubConnection): Promise<void> {
        hub.on('UserAccountArchived', handleUserArchived);
        hub.on('UserAccountEmailChanged', handleUserEmailChanged);
        hub.on('UserAccountFirstNameChanged', handleUserFirstNameChanged);
        hub.on('UserAccountLastNameChanged', handleUserLastNameChanged);
        hub.on('UserAccountRoleAssigned', handleUserRoleAssigned);
        hub.on('UserAccountRoleRemoved', handleUserRoleRemoved);
        hub.on('UserAssignedToPartnerPractice', handleUserAssignedToPartnerPractice);
        hub.on('UserRemovedFromPartnerPractice', handleUserRemovedFromPartnerPractice);
        hub.on('SystemSettingValueChanged', handleSystemSettingValueChanged);
        hub.on('CallStarted', handleCallStarted);
        hub.on('ClientDuplicatesUpdated', handleClientDuplicatesUpdated);
        hub.on('CallEnded', handleCallEnded);
        hub.on('CallbackCreated', handleCallbackCreated);
        hub.on('CallCreated', handleCallCreated);
        hub.on('CallRemoved', handleCallRemoved);
        hub.on('UserAssignedToCall', handleUserAssignedToCall);
        hub.on('UserUnassignedFromCall', handleUserUnassignedFromCall);
        hub.on('DataImportFileStatusUpdated', handleDataImportFileStatusUpdated);
    }
}

export default new WebsocketManager();
